// src/components/MonthViewWithStore.tsx
import React, { useState } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import Grid from '@mui/material/Grid'; 
import { ChevronLeft, ChevronRight, Today } from '@mui/icons-material';
import { Gathering, GatheringStatus } from '../../../../types';
import { format, addMonths, subMonths, eachDayOfInterval, isSameMonth, isSameDay, isToday, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from 'date-fns';
import moment from 'moment'; // Import moment.js
import { useGatheringsForDateRange } from '../../hooks/data/useGatheringsForDateRange';
import { getMonthRange } from '../../../../utils/dateRangeUtils';
import { FilterState } from '../../hooks/useFilters';

interface MonthViewProps {
  token: string | null;
  onViewDetails: (gatheringId: string) => void;
  filters: FilterState;
}

const MonthView: React.FC<MonthViewProps> = ({ 
  token,
  onViewDetails,
  filters
}) => {
  // Manage current date internally
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  
  // Calculate month date range
  const [startDate, endDate] = getMonthRange(currentDate);
  
  // Use the core date range hook directly
  const { 
    gatherings: allMonthGatherings, 
    isLoading, 
    isBackgroundLoading,
    error, 
    refresh 
  } = useGatheringsForDateRange(startDate, endDate, token);
  
  // Apply filters to gatherings
  const monthGatherings = allMonthGatherings.filter(gathering => {
    // Apply space filter
    if (filters.spaceId && gathering.space?.id !== filters.spaceId) {
      return false;
    }

    // Date range filter is not needed here since we're already filtering by month
    // and the date range filter would be redundant with the month view's built-in date range

    // Gathering passed all filters
    return true;
  });
  
  // Navigate to previous month
  const handlePrevMonth = () => {
    setCurrentDate(prevDate => subMonths(prevDate, 1));
  };
  
  // Navigate to next month
  const handleNextMonth = () => {
    setCurrentDate(prevDate => addMonths(prevDate, 1));
  };
  
  // Navigate to today
  const handleToday = () => {
    setCurrentDate(new Date());
  };
  
// Get full weeks range for current month view
const firstDayOfMonth = startOfMonth(currentDate);
const lastDayOfMonth = endOfMonth(currentDate);
const monthStart = startOfWeek(firstDayOfMonth, { weekStartsOn: 0 });
const monthEnd = endOfWeek(lastDayOfMonth, { weekStartsOn: 0 });
const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
  
  // Get gatherings for a specific day
  const getGatheringsForDay = (day: Date) => {
    const gatherings = monthGatherings.filter(gathering => {
      const gatheringDateStr = gathering.date;
      
      // Parse the date string using moment for consistency with WeekViewWithStore
      const gatheringDate = moment(gatheringDateStr).startOf('day').toDate();
      const compareDay = moment(day).startOf('day').toDate();
      
      // Compare dates using isSameDay
      const isSame = moment(gatheringDate).isSame(compareDay, 'day');
      
      return isSame;
    });
    
    return gatherings;
  };
  
  // Format time for display
  const formatTime = (timeString: string): string => {
    // Check if the time already includes AM/PM
    if (timeString.includes('AM') || timeString.includes('PM')) {
      return timeString;
    }
    
    // If not, assume timeString is in format "HH:MM:SS"
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  // Get background color based on gathering status
  const getStatusBackgroundColor = (status: GatheringStatus): string => {
    switch(status) {
      case GatheringStatus.NEW:
        return '#e3f2fd';
      case GatheringStatus.IN_REVIEW:
        return '#fff8e1';
      case GatheringStatus.NEED_MORE_INFO:
        return '#ffebee';
      case GatheringStatus.RESCHEDULE:
        return '#e8f5e9';
      case GatheringStatus.DENIED:
        return '#fafafa';
      case GatheringStatus.AWAITING_GATHERING_LINK:
        return '#e0f7fa';
      case GatheringStatus.READY_TO_PUBLISH:
        return '#f3e5f5';
      case GatheringStatus.PUBLISHED:
        return '#e8f5e9';
      default:
        return 'primary.light';
    }
  };
  
  // Get text color based on gathering status
  const getStatusTextColor = (status: GatheringStatus): string => {
    switch(status) {
      case GatheringStatus.NEW:
        return '#0d47a1';
      case GatheringStatus.IN_REVIEW:
        return '#ff8f00';
      case GatheringStatus.NEED_MORE_INFO:
        return '#c62828';
      case GatheringStatus.RESCHEDULE:
        return '#2e7d32';
      case GatheringStatus.DENIED:
        return '#616161';
      case GatheringStatus.AWAITING_GATHERING_LINK:
        return '#006064';
      case GatheringStatus.READY_TO_PUBLISH:
        return '#6a1b9a';
      case GatheringStatus.PUBLISHED:
        return '#1b5e20';
      default:
        return 'primary.contrastText';
    }
  };
  
  return (
    <Box className="calendar-view">
      {/* Calendar Header */}
      <Box className="calendar-header" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={handlePrevMonth} title="Previous Month">
            <ChevronLeft />
          </IconButton>
          
          <Typography variant="h5" sx={{ mx: 2 }}>
            {format(currentDate, 'MMMM yyyy')}
          </Typography>
          
          <IconButton onClick={handleNextMonth} title="Next Month">
            <ChevronRight />
          </IconButton>
          
          <IconButton onClick={handleToday} title="Today" sx={{ ml: 1 }}>
            <Today />
          </IconButton>
        </Box>
        
        <Typography variant="subtitle2">
          Timezone: EST
        </Typography>
      </Box>
      
      {/* Background loading indicator */}
      {isBackgroundLoading && (
        <Box sx={{ 
          position: 'absolute', 
          top: '60px', 
          right: '20px',
          zIndex: 1000,
          backgroundColor: 'rgba(255,255,255,0.8)',
          padding: '5px 10px',
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <div className="spinner-small"></div>
          <Typography variant="caption">Refreshing...</Typography>
        </Box>
      )}
      
      {/* Error message */}
      {error && (
        <Box sx={{ color: 'error.main', mb: 2, textAlign: 'center' }}>
          {error}
          <button onClick={refresh} className="small-button">Retry</button>
        </Box>
      )}
      
      {/* Loading state */}
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <div className="spinner"></div>
        </Box>
      ) : (
        <>
          {/* Weekday Headers */}
          <Grid container spacing={0} className="calendar-weekdays">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <Grid key={day} size={1.7} sx={{ textAlign: 'center', fontWeight: 'bold', py: 1 }}>
                {day}
              </Grid>
            ))}
          </Grid>
          
          {/* Calendar Grid */}
          <Grid container spacing={0} className="calendar-grid">
            {daysInMonth.map(day => {
              const dayGatherings = getGatheringsForDay(day);
              const isCurrentMonth = isSameMonth(day, currentDate);
              
              return (
                <Grid 
                  key={day.toString()} 
                  size={1.7} 
                  sx={{ 
                    height: '120px', 
                    border: '1px solid #e0e0e0',
                    backgroundColor: isToday(day) ? '#f5f5f5' : 'white',
                    opacity: isCurrentMonth ? 1 : 0.5,
                    p: 1,
                    overflow: 'auto'
                  }}
                >
                  <Typography 
                    variant="body2" 
                    component="div"
                    sx={{ 
                      fontWeight: isToday(day) ? 'bold' : 'normal',
                      color: isToday(day) ? 'primary.main' : 'inherit',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 0.5
                    }}
                  >
                    {format(day, 'd')}
                    {dayGatherings.length > 1 && (
                      <Box 
                        sx={{
                          backgroundColor: 'primary.main',
                          color: 'white',
                          borderRadius: '50%',
                          width: '18px',
                          height: '18px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '0.7rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {dayGatherings.length}
                      </Box>
                    )}
                  </Typography>
                  
                  {dayGatherings.map(gathering => (
                    <Box 
                      key={gathering.id}
                      onClick={() => onViewDetails(gathering.id)}
                      sx={{ 
                        backgroundColor: getStatusBackgroundColor(gathering.status),
                        color: getStatusTextColor(gathering.status),
                        p: 0.5,
                        borderRadius: 1,
                        mb: 0.5,
                        fontSize: '0.75rem',
                        cursor: 'pointer',
                        '&:hover': {
                          filter: 'brightness(0.9)',
                        }
                      }}
                    >
                      <Box sx={{ fontWeight: 'bold' }}>
                        {formatTime(gathering.start_time)}
                      </Box>
                      <Box sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {gathering.title}
                      </Box>
                    </Box>
                  ))}
                </Grid>
              );
            })}
          </Grid>
        </>
      )}
    </Box>
  );
};

export default MonthView;